import {
  DefaultError,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ModuleI,
  ResponseI,
  RecapI,
  ImageI,
  AnalyticsEvent,
  FeedbackI,
  BookmarkTypeEnum,
  ToolboxI,
  AccessCodeI,
  ToolboxQuestionI,
  ToolboxPlanI,
  UserToolboxProfileI,
  ToolboxAnswers,
} from "../types";
import { APIError } from "../utils/ErrorHandling";
import {
  CreateUpdateBookmarkRequest,
  CreateUpdateLessonRequest,
  CreateFeedbackRequest,
  UpdateLegalAcceptanceRequest,
  CreateErrorReportRequest,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
} from "./types";
import * as client from "./client";

interface MutationOptions<T> {
  onMutate?: () => Promise<void>;
  onSuccess?: (data: T) => Promise<void>;
  onError?: (error: APIError) => void;
}

function useGenericMutation<T>(
  mutationFn: (body: T) => Promise<unknown>,
  options: MutationOptions<T>,
) {
  return useMutation<unknown, DefaultError, T>({
    mutationFn,
    onMutate: options.onMutate,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export function useCreateTopic(options: MutationOptions<ModuleI>) {
  return useGenericMutation(client.createTopic, options);
}
export function useUpdateTopic(options: MutationOptions<ModuleI>) {
  return useGenericMutation(client.updateTopic, options);
}

export function useDeleteModule(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteModule, options);
}

export function useCreateLesson(
  options: MutationOptions<CreateUpdateLessonRequest>,
) {
  return useGenericMutation(client.createLesson, options);
}

export function useUpdateLesson(
  options: MutationOptions<CreateUpdateLessonRequest>,
) {
  return useGenericMutation(client.updateLesson, options);
}

export function useDeleteLesson(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteLesson, options);
}

export function useDeletePage(options: MutationOptions<string>) {
  return useGenericMutation(client.deletePage, options);
}

export function useRestorePage(options: MutationOptions<string>) {
  return useGenericMutation(client.restorePage, options);
}

export function useArchivePage(options: MutationOptions<string>) {
  return useGenericMutation(client.archivePage, options);
}

export function useUnarchivePage(options: MutationOptions<string>) {
  return useGenericMutation(client.unarchivePage, options);
}

export function useCreateOrGetRecap(options: MutationOptions<RecapI>) {
  return useGenericMutation(client.createOrGetRecap, options);
}

export function useUpdateRecap(options: MutationOptions<RecapI>) {
  return useGenericMutation(client.updateRecap, options);
}

export function useDeleteRecap(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteRecap, options);
}

export function useDeleteAllRecaps(options: MutationOptions<void>) {
  return useGenericMutation(client.deleteAllRecaps, options);
}

export function useCreateImage(options: MutationOptions<ImageI>) {
  return useMutation<ImageI, DefaultError, FormData>({
    mutationFn: client.createImage,
    onMutate: options.onMutate,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export function useUpdateImage(options: MutationOptions<ImageI>) {
  return useMutation<ImageI, DefaultError, { id: string; data: FormData }>({
    mutationFn: (vars) => client.updateImage(vars.id, vars.data),
    onMutate: options.onMutate,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export function useDeleteImage(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteImage, options);
}

export function useCreateResponse(options: MutationOptions<ResponseI>) {
  return useGenericMutation(client.createResponse, options);
}

export function useDeleteResponse(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteResponse, options);
}

export function useDeleteAllResponses(options: MutationOptions<void>) {
  return useGenericMutation(client.deleteAllResponses, options);
}

export function useCreatePageBookmark(
  options: MutationOptions<CreateUpdateBookmarkRequest>,
) {
  return useGenericMutation(
    (body) => client.createBookmark(BookmarkTypeEnum.PAGE, body),
    options,
  );
}

export function useUpdatePageBookmark(
  options: MutationOptions<CreateUpdateBookmarkRequest>,
) {
  return useGenericMutation(
    (body) => client.updateBookmark(BookmarkTypeEnum.PAGE, body._id, body),
    options,
  );
}

export function useDeletePageBookmark(options: MutationOptions<string>) {
  return useGenericMutation(
    (id) => client.deleteBookmark(BookmarkTypeEnum.PAGE, id),
    options,
  );
}

export function useCreateTopicBookmark(
  options: MutationOptions<CreateUpdateBookmarkRequest>,
) {
  return useGenericMutation(
    (body) => client.createBookmark(BookmarkTypeEnum.MODULE, body),
    options,
  );
}

export function useDeleteTopicBookmark(options: MutationOptions<string>) {
  return useGenericMutation(
    (id) => client.deleteBookmark(BookmarkTypeEnum.MODULE, id),
    options,
  );
}

export function useTrackPageView(options: MutationOptions<AnalyticsEvent>) {
  return useGenericMutation(client.trackPageView, options);
}

export function useTrackEvent(options: MutationOptions<AnalyticsEvent>) {
  return useGenericMutation(client.trackEvent, options);
}

export function useCreateFeedback(
  options: MutationOptions<CreateFeedbackRequest>,
) {
  return useGenericMutation(client.createFeedback, options);
}

export function useCreateLessonFeedback(options: MutationOptions<FeedbackI>) {
  return useGenericMutation(client.createLessonFeedback, options);
}

export function useDeleteFeedback(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteFeedback, options);
}

export function useResolveFeedback(
  options: MutationOptions<{ feedbackId: string; isResolved: boolean }>,
) {
  return useGenericMutation(client.resolveFeedback, options);
}

export function useUpdateUserPreferences(
  options: MutationOptions<{
    saveAnswersToLog: boolean;
    askEveryTime?: boolean;
    emailAccountIssues?: boolean;
    emailFeedbackRequests?: boolean;
    emailNewsUpdates?: boolean;
    videoPlaybackSpeed?: number;
    autoplayVideos?: boolean;
    videoVolume?: number;
  }>,
) {
  return useGenericMutation(
    (body) =>
      client.updateUserPreferences(
        body.saveAnswersToLog,
        body.askEveryTime,
        body.emailAccountIssues,
        body.emailFeedbackRequests,
        body.emailNewsUpdates,
        body.videoPlaybackSpeed,
        body.autoplayVideos,
        body.videoVolume,
      ),
    options,
  );
}

export function useDismissTutorial(
  options: MutationOptions<{ tutorialId: string }>,
) {
  return useGenericMutation(
    (body) => client.dismissTutorial(body.tutorialId),
    options,
  );
}

export function useResetTutorials(
  options: MutationOptions<Record<string, never>>,
) {
  return useGenericMutation(() => client.resetTutorials(), options);
}

export function useUpdateToolboxByTopicId(
  options: MutationOptions<{ moduleId: string; body: Partial<ToolboxI> }>,
) {
  return useGenericMutation(
    ({ moduleId, body }: { moduleId: string; body: Partial<ToolboxI> }) =>
      client.updateToolboxByTopicId(moduleId, body),
    options,
  );
}

export function useSignUpUser(options: {
  onMutate?: () => Promise<void>;
  onSuccess?: (data: { status: string; message: string }) => Promise<void>;
  onError?: (error: DefaultError) => void;
}) {
  return useMutation<
    {
      status: string;
      message: string;
    },
    DefaultError,
    {
      username: string;
      email: string;
      password: string;
      accessCode: string;
      legalAcceptance?: {
        acceptLegal: boolean;
        version: string;
        agreedToEmail?: boolean;
      };
    }
  >({
    mutationFn: async (variables) => {
      return await client.signUpUser(variables);
    },
    onMutate: options.onMutate,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export function useDeleteAllFavorites(options: MutationOptions<void>) {
  return useGenericMutation(client.deleteAllFavorites, options);
}

export function useUpdateLegalAcceptance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateLegalAcceptanceRequest) => {
      return client.updateLegalAcceptance(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-compliance"] });
    },
  });
}

export function useUpdateTutorialViewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (version: string) => {
      return client.updateTutorialViewed(version);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tutorial-status"] });
    },
  });
}

export function useUpdateTutorialVideoWatched() {
  return useMutation({
    mutationFn: () => {
      return client.updateTutorialVideoWatched();
    },
  });
}

export function useCreateErrorReport(
  options: MutationOptions<CreateErrorReportRequest>,
) {
  return useGenericMutation(client.createErrorReport, options);
}

export function useCreateAccessCode(
  options: MutationOptions<{
    code: string;
    limitCount?: number;
    segment: string;
    expirationDate?: Date;
  }>,
) {
  return useGenericMutation(client.createAccessCode, options);
}

export function useUpdateAccessCode(options: {
  onMutate?: () => Promise<void>;
  onSuccess?: (data: AccessCodeI) => Promise<void>;
  onError?: (error: DefaultError) => void;
}) {
  return useMutation<
    AccessCodeI,
    DefaultError,
    { id: string; body: Partial<AccessCodeI> }
  >({
    mutationFn: async (vars) => client.updateAccessCode(vars.id, vars.body),
    onMutate: options.onMutate,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export function useDeleteAccessCode(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteAccessCode, options);
}

export function useCreateAnnouncement(
  options: MutationOptions<CreateAnnouncementRequest>,
) {
  return useGenericMutation(client.createAnnouncement, options);
}

export function useUpdateAnnouncement(
  options: MutationOptions<{ id: string; body: UpdateAnnouncementRequest }>,
) {
  return useGenericMutation(
    ({ id, body }) => client.updateAnnouncement(id, body),
    options,
  );
}

export function useDeleteAnnouncement(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteAnnouncement, options);
}

export function useTrackAnnouncementView(options: MutationOptions<string>) {
  return useGenericMutation(client.trackAnnouncementView, options);
}

export function useDismissAnnouncement(options: MutationOptions<string>) {
  return useGenericMutation(client.dismissAnnouncement, options);
}

export function useTrackAnnouncementAction(
  options: MutationOptions<{ announcementId: string; actionLabel: string }>,
) {
  return useGenericMutation(
    ({ announcementId, actionLabel }) =>
      client.trackAnnouncementAction(announcementId, actionLabel),
    options,
  );
}

export function useRequestAccountDeletion(options: MutationOptions<void>) {
  return useGenericMutation(() => client.requestAccountDeletion(), options);
}

export function useCancelAccountDeletion(options: MutationOptions<void>) {
  return useGenericMutation(() => client.cancelAccountDeletion(), options);
}

export function useSendCurrentEmailVerificationCode(
  options: MutationOptions<void>,
) {
  return useGenericMutation(client.sendCurrentEmailVerificationCode, options);
}

export function useVerifyCurrentEmail(options: MutationOptions<string>) {
  return useGenericMutation(client.verifyCurrentEmail, options);
}

export function useSendEmailChangeVerificationCode(
  options: MutationOptions<string>,
) {
  return useGenericMutation(client.sendEmailChangeVerificationCode, options);
}

export function useVerifyAndChangeEmail(
  options: MutationOptions<{ verificationCode: string; newEmail: string }>,
) {
  return useGenericMutation(client.verifyAndChangeEmail, options);
}

export function useMarkUserForDeletion(options: MutationOptions<string>) {
  return useGenericMutation(client.markUserForDeletion, options);
}

export function useCancelUserDeletion(options: MutationOptions<string>) {
  return useGenericMutation(client.cancelUserDeletion, options);
}

export function useUpdateDisplayName(options: MutationOptions<string>) {
  return useGenericMutation(client.updateDisplayName, options);
}

export function useCreateProfileDefinition(
  options: MutationOptions<{
    topicId: string;
    questions?: ToolboxQuestionI[];
    displayName?: string | null;
  }>,
) {
  return useGenericMutation(client.createProfileDefinition, options);
}

export function useUpdateProfileDefinition(
  options: MutationOptions<{
    definitionId: string;
    questions?: ToolboxQuestionI[];
    displayName?: string | null;
  }>,
) {
  return useGenericMutation(
    ({ definitionId, ...body }) =>
      client.updateProfileDefinition(definitionId, body),
    options,
  );
}

export function useActivateProfileDefinition(options: MutationOptions<string>) {
  return useGenericMutation(client.activateProfileDefinition, options);
}

export function useDeleteProfileDefinition(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteProfileDefinition, options);
}

export function useActivateCheckinDefinition(options: MutationOptions<string>) {
  return useGenericMutation(client.activateCheckinDefinition, options);
}

export function useUpdateCheckinDefinition(
  options: MutationOptions<{
    definitionId: string;
    questions?: ToolboxQuestionI[];
    displayName?: string | null;
    ratingEnabled?: boolean;
    ratingScale?: number;
    ratingLabel?: string;
  }>,
) {
  return useGenericMutation(
    ({
      definitionId,
      questions,
      displayName,
      ratingEnabled,
      ratingScale,
      ratingLabel,
    }) =>
      client.updateCheckinDefinition(definitionId, {
        questions,
        displayName,
        ratingEnabled,
        ratingScale,
        ratingLabel,
      }),
    options,
  );
}

export function useCreateCheckinDefinition(
  options: MutationOptions<{
    topicId: string;
    questions?: ToolboxQuestionI[];
    ratingEnabled?: boolean;
    ratingScale?: number;
    ratingLabel?: string;
  }>,
) {
  return useGenericMutation(client.createCheckinDefinition, options);
}

export function useDeleteCheckinDefinition(options: MutationOptions<string>) {
  return useGenericMutation(client.deleteCheckinDefinition, options);
}

export function useCreateToolboxTopicPlan(
  options: MutationOptions<ToolboxPlanI>,
) {
  return useMutation<
    ToolboxPlanI,
    DefaultError,
    {
      topicId: string;
      body: { goal: string; cue: string; timeframe: string };
    }
  >({
    mutationFn: ({ topicId, body }) =>
      client.postToolboxTopicPlan(topicId, body),
    onMutate: options.onMutate,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export function usePatchToolboxPlan(options: MutationOptions<ToolboxPlanI>) {
  return useMutation<
    ToolboxPlanI,
    DefaultError,
    {
      planId: string;
      body: { goal?: string; cue?: string; timeframe?: string };
    }
  >({
    mutationFn: ({ planId, body }) => client.patchToolboxPlan(planId, body),
    onMutate: options.onMutate,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

export function useDeleteToolboxPlan(options?: MutationOptions<void>) {
  return useMutation<void, DefaultError, { planId: string; topicId: string }>({
    mutationFn: ({ planId }) => client.deleteToolboxPlan(planId),
    onMutate: options?.onMutate,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useReorderProfileDefinitions(
  options: MutationOptions<{
    topicId: string;
    orderedDefinitionIds: string[];
  }>,
) {
  return useGenericMutation(
    ({ topicId, orderedDefinitionIds }) =>
      client.reorderProfileDefinitions(topicId, orderedDefinitionIds),
    options,
  );
}

export function useUpsertUserToolboxProfile(
  options: MutationOptions<UserToolboxProfileI>,
) {
  const queryClient = useQueryClient();
  return useMutation<
    UserToolboxProfileI,
    DefaultError,
    { topicId: string; answers: ToolboxAnswers }
  >({
    mutationFn: ({ topicId, answers }) =>
      client.upsertUserToolboxProfile(topicId, answers),
    onMutate: options.onMutate,
    onSuccess: async (data, { topicId }) => {
      await queryClient.invalidateQueries({
        queryKey: ["userToolboxProfile", topicId],
      });
      if (options.onSuccess) await options.onSuccess(data);
    },
    onError: options.onError,
  });
}
